import type { PhaseCardValue } from "./PhaseCard";

/**
 * T63 Part O §Q — single source of truth for what a workflow phase's
 * header chip should show. Prevents PhaseCard.tsx from rendering
 * competing destination chips ("On mastery → Mastered" *and* "Closed").
 *
 * Precedence (matches runtime ApplyMasteryOutcomeService):
 *   1. Explicit `masteredTargetStatus` → the target lifecycle status
 *      wins. The Next Phase is ignored at runtime.
 *   2. Otherwise `transitionsTo` → the phase advances the target here.
 *   3. Otherwise, if the phase is terminal → the workflow ends.
 *   4. Otherwise → no chip.
 */
export type EffectiveDestination =
  | { kind: "target_status"; value: string }
  | { kind: "next_phase";    value: string }
  | { kind: "terminal_end" }
  | { kind: "none" };

export function getEffectivePhaseDestination(phase: PhaseCardValue): EffectiveDestination {
  const mastered = (phase.masteredTargetStatus || "").trim();
  if (mastered) return { kind: "target_status", value: mastered };
  const next = (phase.transitionsTo || "").trim();
  if (next) return { kind: "next_phase", value: next };
  if (phase.isTerminal) return { kind: "terminal_end" };
  return { kind: "none" };
}

/**
 * Standard HiRasmus-aligned phase type detection. New workflows are
 * seeded with the exact names below (see standardWorkflowFactory), so
 * name-matching is the pragmatic contract until a first-class
 * `phase_type` column exists. Case-insensitive, trimmed.
 */
export type StandardPhaseType = "probe" | "acquisition" | "mastered" | "custom";

export function getStandardPhaseType(name: string | undefined): StandardPhaseType {
  const n = (name ?? "").trim().toLowerCase();
  if (n === "probe") return "probe";
  if (n === "acquisition") return "acquisition";
  if (n === "mastered") return "mastered";
  return "custom";
}

/** Broaden the previous "Maintenance"-only interval predicate — Mastered
 *  is HiRasmus's maintenance phase, so its card gets the interval stack
 *  too (T63 Part O §H). Both share the same phase_intervals FK. */
export function isMaintenanceCapablePhase(name: string | undefined): boolean {
  const n = (name ?? "").trim().toLowerCase();
  return n === "maintenance" || n === "mastered";
}
